import AddIcon from "@mui/icons-material/Add"
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeftOutlined"
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRightOutlined"
import CheckIcon from "@mui/icons-material/Check"
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined"
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined"
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined"
import MultipleStopOutlinedIcon from "@mui/icons-material/MultipleStopOutlined"
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import ReorderOutlinedIcon from "@mui/icons-material/ReorderOutlined"
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import SortOutlinedIcon from "@mui/icons-material/SortOutlined"
import Button from "@mui/material/Button"
import ButtonGroup from "@mui/material/ButtonGroup"
import Chip from "@mui/material/Chip"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Skeleton from "@mui/material/Skeleton"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { grey } from "@mui/material/colors"
import { styled } from "@mui/material/styles"
import { useEffect, useRef, useState } from "react"
import { DndProvider, useDrag, useDrop } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { useDispatch, useSelector } from "react-redux"
import { Link as RouterLink } from "react-router-dom"
import { useDebounceValue } from "usehooks-ts"

import TabData from "../../../common/TabData"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column, { PaddedContentColumn } from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import { BaseGlassMixin } from "../../../common/components/GlassMixin"
import PageTopSection from "../../../common/components/PageTopSection"
import Row, { RowButColumnOnMobile } from "../../../common/components/Row"
import TabContent, {
  AlwaysPresentTabContent,
} from "../../../common/components/TabContent"
import { getTableSearchParams } from "../../../common/components/tables/utils"
import SegmentTracker from "../../../common/tracking"
import {
  formatCurrency,
  trimLongString,
  useAutoNavigateSearchParamState,
} from "../../../common/utils"
import { selectProfileLoaded, selectSubscription } from "../../users/reducer"
import BorderedGrayChip from "../components/BorderedGrayChip"
import BulkSendEmailCampaignDialogCombo, {
  useBulkSendEmailCampaignDialogComboState,
} from "../components/BulkSendEmailCampaignDialogCombo"
import {
  CandidateActionDialogs,
  useCandidateActions,
} from "../components/CandidateActionController"
import CandidateAvatar from "../components/CandidateAvatar"
import InfiniteScroll from "../components/InfiniteScroll"
import AddStageDialog from "./AddStageDialog"
import DeleteStageDialog from "./DeleteStageDialog"
import RenameStageDialog from "./RenameStageDialog"
import SummaryTab from "./SummaryTab"

export default function Pipeline() {
  const [activeTabIndex, setActiveTabIndex] = useAutoNavigateSearchParamState(
    "tab",
    0
  )
  const [stages, setStages] = useState(
    ["New", "Screening", "Meeting", "Warming", "Proposal Sent", "Hired"].map(
      (stageTitle, i) => ({
        id: i,
        name: stageTitle,
        order: i,
        fake: true,
      })
    )
  )

  const [movedCandidateData, setMovedCandidateData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [addStageDialogOpen, setAddStageDialogOpen] = useState(false)
  const [searchText, setSearchText] = useAutoNavigateSearchParamState("q", "")
  const debouncedSearchText = useDebounceValue(searchText)[0]
  const {
    open: openSnackbar,
    isOpen: snackbarOpen,
    message: snackbarMessage,
    close: closeSnackbar,
  } = useCloseableSnackbarState()
  const axios = useAuthorizedAxiosClient()
  const profileLoaded = useSelector(selectProfileLoaded)

  const candidateActions = useCandidateActions(openSnackbar)

  function handleCandidateMoved(targetColumnIndex) {
    return (candidateItem: CandidateItem, targetStage) => {
      setMovedCandidateData(
        new MovedCandidate(
          candidateItem.candidate,
          stages[candidateItem.sourceColumnIndex].id,
          targetStage.id
        )
      )
    }
  }

  function handleAddStageDialogClosed(newStage) {
    if (newStage) {
      setStages([...stages, { ...newStage, ...new StageExtraProperties() }])
      openSnackbar("Stage created successfully")
    }
    setAddStageDialogOpen(false)
  }

  function handleStageRenamed(updatedStage) {
    setStages(
      stages.map((stage) =>
        stage.id === updatedStage.id ? updatedStage : stage
      )
    )
  }

  function handleStageMoved(sourceStageIndex, targetStageIndex) {
    setStages(arraySwap(stages, sourceStageIndex, targetStageIndex))
    setIsLoading(true)
    axios
      .post(`/crm/api/crm-stages/${stages[sourceStageIndex].id}/reorder/`, {
        to_order: stages[targetStageIndex].order,
      })
      .finally(() => setIsLoading(false))
  }

  function handleStageDeleted(deletedStage, moveToStage) {
    let snackbarMessage = "Pipeline stage deleted successfully"
    if (moveToStage) {
      snackbarMessage = `Pipeline stage deleted. ${deletedStage.candidates.length} candidates moved.`
    }
    openSnackbar(snackbarMessage)
    setStages(stages.filter((stage) => stage.id !== deletedStage.id))
  }

  useEffect(
    () => {
      if (!profileLoaded) {
        return
      }
      setIsLoading(true)
      axios
        .get("/crm/api/crm-stages/")
        .then(({ data: stages }) => {
          setStages(stages)
        })
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileLoaded]
  )

  return (
    <Column>
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <PageTopSection title="Pipeline" />
      <PaddedContentColumn>
        <RowButColumnOnMobile
          sx={{
            justifyContent: { xs: "flex-start", sm: "space-between" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            pb: 2,
          }}
        >
          <ButtonGroup variant="outlined" color="inherit" size="small">
            {TABS.map((tabData, i) => (
              <Button
                startIcon={<tabData.icon />}
                className={i === activeTabIndex ? "active" : undefined}
                onClick={() => setActiveTabIndex(i)}
                key={i}
              >
                {tabData.title}
              </Button>
            ))}
          </ButtonGroup>
          <TabContent index={0} value={activeTabIndex}>
            <RowButColumnOnMobile
              sx={{ gap: 2, alignItems: { xs: "stretch", sm: "center" } }}
            >
              <TextField
                placeholder="Search candidates"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: "220px" } }}
              />
              <Button
                variant="contained"
                color="primary"
                endIcon={<AddIcon />}
                onClick={() => setAddStageDialogOpen(true)}
              >
                Add Stage
              </Button>
              <AddStageDialog
                open={addStageDialogOpen}
                onClose={handleAddStageDialogClosed}
                existingStages={stages}
              />
            </RowButColumnOnMobile>
          </TabContent>
        </RowButColumnOnMobile>
        <AlwaysPresentTabContent index={0} value={activeTabIndex}>
          <DndProvider backend={HTML5Backend}>
            <Row gap={3} sx={{ overflowX: "auto" }}>
              {stages.map((stage, i) => (
                <StageColumn
                  key={i}
                  index={i}
                  stage={stage}
                  onCandidateMoved={handleCandidateMoved(i)}
                  movedCandidateData={movedCandidateData}
                  clearMovedCandidateData={() => setMovedCandidateData(null)}
                  stagesLoaded={!isLoading}
                  stages={stages}
                  onStageRenamed={handleStageRenamed}
                  onStageMoved={(targetIndex) =>
                    handleStageMoved(i, targetIndex)
                  }
                  onStageDeleted={handleStageDeleted}
                  debouncedSearchText={debouncedSearchText}
                  onEmailClick={candidateActions.handleEmailButtonClicked}
                  onSMSClick={candidateActions.handleSMSButtonClicked}
                  onCallClick={candidateActions.handleCallButtonClicked}
                />
              ))}
            </Row>
          </DndProvider>
        </AlwaysPresentTabContent>
        <TabContent index={1} value={activeTabIndex}>
          <SummaryTab isLoading={isLoading} setIsLoading={setIsLoading} />
        </TabContent>
      </PaddedContentColumn>
      <CloseableSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
      <CandidateActionDialogs
        actionsState={candidateActions}
        openSnackbar={openSnackbar}
      />
    </Column>
  )
}

function StageColumn({
  index,
  stage,
  onCandidateMoved,
  movedCandidateData,
  clearMovedCandidateData,
  stagesLoaded,
  stages,
  onStageMoved,
  onStageRenamed,
  onStageDeleted,
  debouncedSearchText,
  onEmailClick,
  onSMSClick,
  onCallClick,
}) {
  const [stageDetails, setStageDetails] = useState(new StageExtraProperties())
  const [isLoading, setIsLoading] = useState(false)
  const [{ isOver }, dropRef] = useDrop({
    accept: DRAG_TYPE_CANDIDATE_CARD,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    canDrop: (item: CandidateItem) => item.sourceColumnIndex !== index,
    drop: (item: CandidateItem) => {
      onCandidateMoved(item, stageDetails)
    },
  })
  const [menuButtonRef, setMenuButtonRef] = useState(null)
  const [sortSubMenuParentMenuItemRef, setSortSubMenuParentMenuItemRef] =
    useState(null)
  const [renameStageDialogOpen, setRenameStageDialogOpen] = useState(false)
  const [deleteStageDialogOpen, setDeleteStageDialogOpen] = useState(false)
  const {
    bulkSendEmailCampaignDialogOpen,
    closeSnackbar,
    confirmConnectGmailDialogOpen,
    handleBulkSendEmailCampaignDialogClosed,
    handleConfirmConnectGmailDialogClosed,
    handleSendEmailButtonClicked,
    isSendingBulkCampaign,
    isSnackbarOpen,
    setIsSendingBulkCampaign,
    snackbarMessage,
  } = useBulkSendEmailCampaignDialogComboState()
  const axios = useAuthorizedAxiosClient()
  const candidates = stageDetails.candidates
  const columnID = `stage-candidates-${index}`
  const NEW_STAGE_NAME = "New"
  const stageEditable =
    [NEW_STAGE_NAME, "Intro Call Set", "Intro Call Booked", "Hired"].indexOf(
      stage.name
    ) > -1

  function closeMenu() {
    setSortSubMenuParentMenuItemRef(null)
    setMenuButtonRef(null)
  }

  function getStageCandidates(urlParams = {}) {
    return axios.get(`/crm/api/crm-stages/${stage.id}/candidates/`, {
      params: urlParams,
    })
  }

  function handleDeleteMenuItemClicked() {
    setDeleteStageDialogOpen(true)
    closeMenu()
  }

  function handleDeleteStageDialogClosed(stageDeleted, moveToStage) {
    setDeleteStageDialogOpen(false)
    if (stageDeleted) {
      onStageDeleted(stageDetails, moveToStage)
    }
  }

  function handleCandidateSwapped(sourceCandidateIndex, targetCandidateIndex) {
    const reorderedCandidates = arraySwap(
      stageDetails.candidates,
      sourceCandidateIndex,
      targetCandidateIndex
    )
    updateCandidates(
      reorderedCandidates,
      stageDetails.totalResults,
      stageDetails.totalAnnualValue
    )
    setIsLoading(true)
    axios
      .post(`/crm/api/reorder-stage-candidates/${stageDetails.id}/`, {
        stage_candidate_indexes: Object.fromEntries(
          reorderedCandidates.map((candidate, i) => [candidate.id, i])
        ),
      })
      .finally(() => setIsLoading(false))
  }

  function handleRenameMenuItemClicked() {
    setRenameStageDialogOpen(true)
    closeMenu()
  }

  function handleRenameStageDialogClosed(updatedStage) {
    if (updatedStage) {
      onStageRenamed(updatedStage)
    }
    setRenameStageDialogOpen(false)
  }

  function handleMoveStageMenuItemClicked(direction) {
    return () => {
      let targetIndex = index + 1
      if (direction === "left") {
        targetIndex = index - 1
      }
      onStageMoved(targetIndex)
      closeMenu()
    }
  }

  function handleSortMenuItemClicked(stageOrdering) {
    setStageDetails({ ...stageDetails, ordering: stageOrdering })
    closeMenu()
  }

  function handleSendEmailTemplateMenuItemClicked() {
    handleSendEmailButtonClicked()
    closeMenu()
  }

  function handleBulkSendEmailCampaignButtonClicked(selectedCampaign) {
    setIsSendingBulkCampaign(true)
    axios
      .post(
        `/workflows/api/email/send-campaign/${selectedCampaign.id}/crm-stage/${stage.id}/`
      )
      .then(() => handleBulkSendEmailCampaignDialogClosed(true))
      .finally(() => setIsSendingBulkCampaign(false))
  }

  function updateCandidates(newCandidates, totalResults, totalAnnualValue) {
    setStageDetails((stageDetails) => ({
      ...stageDetails,
      candidates: newCandidates,
      totalResults,
      totalAnnualValue,
    }))
  }

  function queryCandidates(pageIndex = stageDetails.pageIndex) {
    setIsLoading(true)
    return getStageCandidates(
      getTableSearchParams(
        debouncedSearchText,
        pageIndex,
        null,
        stageDetails.ordering
      )
    ).finally(() => setIsLoading(false))
  }

  useEffect(
    () => {
      if (stage.fake) {
        return
      }
      queryCandidates().then(({ data: candidatesPage }) =>
        setStageDetails({
          ...stage,
          ...new StageExtraProperties(
            candidatesPage.results,
            1,
            candidatesPage.count,
            candidatesPage.total_annual_value
          ),
        })
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stage]
  )

  useEffect(
    () => {
      if (stageDetails.pageIndex === 1) {
        return
      }
      // append candidates to stage
      queryCandidates().then(({ data: candidatesPage }) =>
        updateCandidates(
          [...stageDetails.candidates, ...candidatesPage.results],
          stageDetails.totalResults,
          stageDetails.totalAnnualValue
        )
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stageDetails.pageIndex, stage]
  )

  useEffect(
    () => {
      // replace stage candidates
      // prevent loading candidates here which won't populate stageDetails from stage
      if (!stageDetails.name) {
        return
      }
      setStageDetails({ ...stageDetails, pageIndex: 1 })
      queryCandidates(1).then(({ data: candidatesPage }) =>
        updateCandidates(
          candidatesPage.results,
          candidatesPage.count,
          candidatesPage.total_annual_value
        )
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchText, stageDetails.ordering]
  )

  useEffect(
    () => {
      if (!movedCandidateData) {
        return
      }
      if (movedCandidateData.sourceStageID === stageDetails.id) {
        updateCandidates(
          stageDetails.candidates.filter(
            (candidate) => candidate.id !== movedCandidateData.candidate.id
          ),
          stageDetails.totalResults - 1,
          stageDetails.totalAnnualValue -
            (movedCandidateData.candidate.annual_value || 0)
        )
      } else if (movedCandidateData.targetStageID === stageDetails.id) {
        setIsLoading(true)
        axios
          .put(
            `/crm/api/move-candidate-to-stage/${movedCandidateData.candidate.id}/`,
            {
              stage_id: stageDetails.id,
            }
          )
          .then(() => {
            updateCandidates(
              [...stageDetails.candidates, movedCandidateData.candidate],
              stageDetails.totalResults + 1,
              stageDetails.totalAnnualValue +
                (movedCandidateData.candidate.annual_value || 0)
            )
            clearMovedCandidateData()
            SegmentTracker.track("Candidate moved to new stage", {
              "Candidate name": movedCandidateData.candidate.candidate_name,
              "Source stage": stages.find(
                (stage) => stage.id === movedCandidateData.sourceStageID
              ).name,
              "Target stage": stages.find(
                (stage) => stage.id === movedCandidateData.targetStageID
              ).name,
            })
          })
          .finally(() => setIsLoading(false))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [movedCandidateData]
  )

  return (
    <Column sx={{ minWidth: "324px", position: "relative" }}>
      <RenameStageDialog
        open={renameStageDialogOpen}
        onClose={handleRenameStageDialogClosed}
        stage={stage}
        existingStages={stages}
      />
      <DeleteStageDialog
        open={deleteStageDialogOpen}
        onClose={handleDeleteStageDialogClosed}
        stageToDelete={stageDetails}
        stages={stages}
      />
      <StageHeaderContainer>
        <Row sx={{ alignItems: "start", flexDirection: "column", gap: 1 }}>
          <Typography variant="h3" title={stageDetails.name}>
            {stageDetails.name ? trimLongString(stageDetails.name, 10) : ""}
          </Typography>
          <StageCandidateCountChip
            label={`${candidates.length} /${stageDetails.totalResults}`}
          />
        </Row>
        <Row sx={{ alignItems: "center", gap: 1 }}>
          <Column alignItems="flex-end" sx={{ gap: 0.5 }}>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(stageDetails.totalAnnualValue)}
            </Typography>
            <Typography variant="body1" color="text.secondary" fontSize="12px">
              Total Annual Value
            </Typography>
          </Column>
          <IconButton
            size="small"
            onClick={(e) => setMenuButtonRef(e.currentTarget)}
          >
            <MoreVertOutlinedIcon />
          </IconButton>
          <Menu
            open={!!menuButtonRef}
            onClose={closeMenu}
            anchorEl={menuButtonRef}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            transformOrigin={{ horizontal: "right" }}
          >
            <MenuItem
              disabled={
                index === 0 ||
                /* prevent moving the stage on the right to the New stage to the left */ stages[
                  index - 1
                ]?.name === NEW_STAGE_NAME
              }
              onClick={handleMoveStageMenuItemClicked("left")}
            >
              <ListItemIcon>
                <ArrowCircleLeftIcon />
              </ListItemIcon>
              <ListItemText>Move left</ListItemText>
            </MenuItem>
            <MenuItem
              disabled={
                stageDetails.name === NEW_STAGE_NAME ||
                index === stages.length - 1
              }
              onClick={handleMoveStageMenuItemClicked("right")}
            >
              <ListItemIcon>
                <ArrowCircleRightIcon />
              </ListItemIcon>
              <ListItemText>Move right</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleRenameMenuItemClicked}
              disabled={stageEditable}
            >
              <ListItemIcon>
                <EditOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Rename</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleDeleteMenuItemClicked}
              disabled={stageEditable}
            >
              <ListItemIcon>
                <DeleteOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Delete...</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleSendEmailTemplateMenuItemClicked}>
              <ListItemIcon>
                <EmailOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Send email template</ListItemText>
            </MenuItem>
            <MenuItem
              onMouseEnter={(e) =>
                setSortSubMenuParentMenuItemRef(e.currentTarget)
              }
            >
              <ListItemIcon>
                <SortOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Sort by</ListItemText>
              <Menu
                open={!!sortSubMenuParentMenuItemRef}
                onClose={closeMenu}
                anchorEl={sortSubMenuParentMenuItemRef}
                anchorOrigin={{ horizontal: "right", vertical: "center" }}
                transformOrigin={{ horizontal: "left", vertical: "center" }}
              >
                {STAGE_ORDERINGS.map((ordering, i) => (
                  <MenuItem
                    onClick={() =>
                      handleSortMenuItemClicked(ordering.orderingQuery)
                    }
                    key={i}
                  >
                    <SortListItemIcon
                      stageDetails={stageDetails}
                      ordering={ordering.orderingQuery}
                    />
                    <ListItemText>{ordering.name}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </MenuItem>
          </Menu>
          <BulkSendEmailCampaignDialogCombo
            bulkSendEmailCampaignDialogOpen={bulkSendEmailCampaignDialogOpen}
            candidateCount={stageDetails.totalResults}
            closeSnackbar={closeSnackbar}
            confirmConnectGmailDialogOpen={confirmConnectGmailDialogOpen}
            handleBulkSendEmailCampaignDialogClosed={
              handleBulkSendEmailCampaignDialogClosed
            }
            handleConfirmConnectGmailDialogClosed={
              handleConfirmConnectGmailDialogClosed
            }
            isSending={isSendingBulkCampaign}
            isSnackbarOpen={isSnackbarOpen}
            onSendEmailCampaignButtonClicked={
              handleBulkSendEmailCampaignButtonClicked
            }
            snackbarMessage={snackbarMessage}
          />
        </Row>
      </StageHeaderContainer>
      <CandidatesColumn
        isCardOver={isOver}
        isStageLoading={isLoading}
        ref={dropRef}
        id={columnID}
      >
        <InfiniteScroll
          hasMorePages={candidates.length < stageDetails.totalResults}
          onNextPage={() =>
            setStageDetails({
              ...stageDetails,
              pageIndex: stageDetails.pageIndex + 1,
            })
          }
          scrollTargetId={columnID}
        >
          {stagesLoaded ? (
            candidates.map((candidate, i) => (
              <CandidateCard
                key={i}
                candidate={candidate}
                index={i}
                columnIndex={index}
                onCandidatesSwapped={(sourceCandidateIndex) =>
                  handleCandidateSwapped(sourceCandidateIndex, i)
                }
                stage={stage}
                stages={stages}
                onCandidateMoved={onCandidateMoved}
                onEmailClick={onEmailClick}
                onSMSClick={onSMSClick}
                onCallClick={onCallClick}
              />
            ))
          ) : (
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={CANDIDATE_CARD_HEIGHT}
            />
          )}
        </InfiniteScroll>
      </CandidatesColumn>
    </Column>
  )
}

const DRAG_TYPE_CANDIDATE_CARD = "candidateCard"

const CANDIDATE_CARD_HEIGHT = "156px"

function CandidateCard({
  candidate,
  index,
  columnIndex,
  onCandidatesSwapped,
  stage,
  stages,
  onCandidateMoved,
  onEmailClick,
  onSMSClick,
  onCallClick,
}) {
  const ref = useRef(null)
  const [menuAnchorElem, setMenuAnchorElem] = useState(null)
  const [{ isDragging }, dragRef] = useDrag({
    type: DRAG_TYPE_CANDIDATE_CARD,
    item: new CandidateItem(candidate, columnIndex, index),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const [{ handlerID }, dropRef] = useDrop({
    accept: DRAG_TYPE_CANDIDATE_CARD,
    collect(monitor) {
      return {
        handlerID: monitor.getHandlerId(),
      }
    },
    drop(item: CandidateItem, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.cardIndex
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // don't drop if not same column
      if (item.sourceColumnIndex !== columnIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      onCandidatesSwapped(dragIndex)
      item.cardIndex = index
    },
  })

  function handleMoveToStageMenuItemClicked(stage_) {
    onCandidateMoved(new CandidateItem(candidate, columnIndex, index), stage_)
    closeMenu()
  }

  function closeMenu() {
    setMenuAnchorElem(null)
  }

  return (
    <CandidateCardContainer
      sx={{
        opacity: isDragging ? 0.5 : 1,
      }}
      ref={dragRef(dropRef(ref))}
      data-handler-id={handlerID}
    >
      <Column sx={{ justifyContent: "space-between", gap: 2 }}>
        <Row justifyContent="space-between">
          <Row sx={{ alignItems: "center", gap: 1 }}>
            <CandidateAvatar
              candidate={candidate}
              size="32px"
              fontSize="14px"
            />
            <Link
              variant="h5"
              fontWeight={500}
              color="inherit"
              component={RouterLink}
              to={`/candidates/${candidate.id}`}
            >
              {trimLongString(candidate.candidate_name, 18)}
            </Link>
          </Row>
          <IconButton onClick={(e) => setMenuAnchorElem(e.currentTarget)}>
            <MultipleStopOutlinedIcon />
          </IconButton>
        </Row>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 5 }}>
          <Tooltip title="Call">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => onCallClick(e, candidate)}
              sx={{
                bgcolor: "colors.brand50",
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "colors.brand200",
                "&:hover": { bgcolor: "colors.brand100" },
              }}
            >
              <PhoneOutlinedIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="SMS">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => onSMSClick(e, candidate)}
              sx={{
                bgcolor: "colors.brand50",
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "colors.brand200",
                "&:hover": { bgcolor: "colors.brand100" },
              }}
            >
              <SmsOutlinedIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Email">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => onEmailClick(e, candidate)}
              sx={{
                bgcolor: "colors.brand50",
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "colors.brand200",
                "&:hover": { bgcolor: "colors.brand100" },
              }}
            >
              <EmailOutlinedIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Menu
          anchorEl={menuAnchorElem}
          open={!!menuAnchorElem}
          onClose={closeMenu}
          anchorOrigin={{
            horizontal: "center",
            vertical: "center",
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
        >
          {stages
            .filter((stage_) => stage_.id !== stage.id)
            .map((stage) => (
              <MenuItem
                key={stage.id}
                onClick={() => handleMoveToStageMenuItemClicked(stage)}
              >
                {stage.name}
              </MenuItem>
            ))}
        </Menu>
        <Row sx={{ justifyContent: "space-between" }}>
          <Row alignItems="center" gap={1}>
            <BorderedGrayChip
              label={candidate.homes_sold_last_year || "Unknown"}
              icon={<HomeOutlinedIcon />}
              title="Homes sold last year"
            />
            <BorderedGrayChip
              label={
                candidate.sales_volume_last_year
                  ? formatCurrency(candidate.sales_volume_last_year, false)
                  : "Unknown"
              }
              icon={<PaidOutlinedIcon />}
              title="Sales volume last year"
            />
          </Row>
          <Column
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Column>
              <Typography variant="h6" fontSize="14px" fontWeight={700}>
                {candidate.annual_value
                  ? formatCurrency(candidate.annual_value)
                  : "Unknown"}
              </Typography>
              <Typography
                variant="body2"
                fontSize="12px"
                color="text.secondary"
              >
                Annual Value
              </Typography>
            </Column>
          </Column>
        </Row>
      </Column>
    </CandidateCardContainer>
  )
}

function SortListItemIcon({ stageDetails, ordering }) {
  return (
    <ListItemIcon>
      {stageDetails.ordering === ordering && <CheckIcon />}
    </ListItemIcon>
  )
}

const CandidatesColumn = styled(Column, {
  shouldForwardProp: (prop) =>
    ["isCardOver", "isStageLoading"].indexOf(prop) === -1,
})(({ theme, isCardOver, isStageLoading }) => ({
  position: "relative",
  marginTop: theme.spacing(2),
  padding: theme.spacing(0.5),
  height: "calc(100vh - 430px)",
  overflowY: "auto",
  gap: theme.spacing(2),
  backgroundColor: isCardOver ? grey[300] : "inherit",
  transition: theme.transitions.create(["background-color"], { duration: 500 }),
  filter: isStageLoading ? "blur(1.5px)" : "blur(0)",
  [theme.breakpoints.only("xs")]: {
    height: "calc(100vh - 240px)",
  },
}))

const CandidateCardContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "12px",
  height: CANDIDATE_CARD_HEIGHT,
  background: BaseGlassMixin.background,
  boxShadow: "rgba(0, 0, 0, 0.24) 0px 2px 6px",
  "&:hover": {
    background: BaseGlassMixin.background,
  },
  cursor: "grab",
}))

const StageHeaderContainer = styled(Row)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  position: "sticky",
  top: 0,
  marginTop: theme.spacing(1),
  paddingRight: theme.spacing(1),
}))

const StageCandidateCountChip = styled((props) => (
  <Chip variant="outlined" {...props} />
))(({ theme }) => ({
  borderRadius: "6px",
  borderColor: theme.palette.colors.gray300,
  boxShadow: "0px 1px 2px 0px #1018280D",
  fontSize: "14px",
  fontWeight: 500,
}))

class StageOrdering {
  constructor(name, orderingQuery) {
    this.name = name
    this.orderingQuery = orderingQuery
  }
}

const MANUAL_ORDERING = new StageOrdering("Manual", "crm_stage_order")

const STAGE_ORDERINGS = [
  new StageOrdering("Latest", "-created"),
  new StageOrdering("Oldest", "created"),
  MANUAL_ORDERING,
]

class StageExtraProperties {
  constructor(
    candidates = [],
    pageIndex = 1,
    totalResults = 0,
    totalAnnualValue = 0,
    ordering = MANUAL_ORDERING.orderingQuery
  ) {
    this.candidates = candidates
    this.pageIndex = pageIndex
    this.totalResults = totalResults
    this.totalAnnualValue = totalAnnualValue
    this.ordering = ordering
  }
}

class CandidateItem {
  constructor(candidate, sourceColumnIndex: Number, cardIndex: Number) {
    this.candidate = candidate
    this.sourceColumnIndex = sourceColumnIndex
    this.cardIndex = cardIndex
  }
}

class MovedCandidate {
  constructor(candidate, sourceStageID, targetStageID) {
    this.candidate = candidate
    this.sourceStageID = sourceStageID
    this.targetStageID = targetStageID
  }
}

function arraySwap(array, sourceIndex, targetIndex) {
  return array.map((item, i) =>
    i === sourceIndex
      ? array[targetIndex]
      : i === targetIndex
      ? array[sourceIndex]
      : item
  )
}

const PipelinesTab = new TabData("Pipelines", ReorderOutlinedIcon)
const TABS = [PipelinesTab, new TabData("Summary", FilterListOutlinedIcon)]
